import React from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

export type ogTypes = "default" | "blog" | "usecase" | "news";

interface SeoProps {
  description?: string;
  lang?: string;
  meta?: Array<{ name: string; content: string; property?: string }>;
  title?: string;
  blog?: boolean;
  useCase?: boolean;
  ogImage?: ogTypes;
}


/**
 * buy.multiagentbook.com and multiagentbook.com serve a byte-identical title
 * and description, and buy allowed all crawling, so the two domains competed
 * for the same query and Google could pick the checkout as the landing page.
 *
 * The split is: www ranks, buy converts. Nothing is removed from buy — a
 * visitor arriving from an email or an ad still gets the full pitch — but the
 * marketing pages declare www as canonical so the ranking signal consolidates
 * there, and the transactional routes are noindex because nobody should reach
 * a checkout from a search result.
 */
const WWW = "https://multiagentbook.com";

/** buy path -> its equivalent on www, or null when there is no equivalent */
const CANONICAL_ON_WWW: Record<string, string> = {
  "/": `${WWW}/`,
  "/privacy": `${WWW}/privacy`,
};

/** pure transaction or account surfaces; keep them out of the index entirely */
const NOINDEX = [/^\/dashboard/, /^\/login/, /^\/success/, /^\/checkout/];

const siteMetadata = {
  defaultTitle: "Designing Multi-Agent Systems",
  defaultDescription: "Access your book downloads, manage your account, and explore exclusive content for Designing Multi-Agent Systems",
  author: "Victor Dibia",
  siteUrl: "https://buy.multiagentbook.com",
  twitterUsername: "@designingwithml",
  keywords: "multi-agent, customer platform, dashboard, payments"
};

function SEO({
  description = "",
  lang = "en",
  meta = [],
  title,
  blog = false,
  useCase = false,
  ogImage,
}: SeoProps) {
  const {
    defaultTitle,
    defaultDescription,
    siteUrl,
    twitterUsername,
    author,
    keywords,
  } = siteMetadata;

  const getImageSrc = () => {
    // First priority: Explicit selection via ogImage
    if (ogImage === "blog") {
      return "/images/og-card.png";
    }
    if (ogImage === "usecase") {
      return "/images/og-card-usecases.png";
    }
    if (ogImage === "news") {
      return "/images/og-card-news.png";
    }
    if (ogImage === "default") {
      return "/images/og-card.png";
    }

    // Second priority: Based on page type (backwards compatibility)
    if (blog) {
      return "/images/og-card.png";
    }
    if (useCase) {
      return "/images/og-card-usecases.png";
    }

    // Default fallback
    return "/images/og-card.png";
  };

  const seo = {
    title: title || defaultTitle,
    titleTemplate: title
      ? `${title} | ${defaultTitle}`
      : `${defaultTitle} | ${defaultTitle}`,
    description: description || `${title} | ${defaultDescription}`,
    image: `${siteUrl}${getImageSrc()}`,
    url: `${siteUrl}`,
  };

  const location = useLocation();
  const cleanPath = (location.pathname || "/").replace(/\/+$/, "") || "/";
  const canonicalHref = CANONICAL_ON_WWW[cleanPath] || `${siteUrl}${cleanPath === "/" ? "/" : cleanPath}`;
  const noindex = NOINDEX.some((re) => re.test(cleanPath));

  const metaTags = [
    {
      name: `description`,
      content: seo.description,
    },
    {
      name: `author`,
      content: author,
    },
    {
      name: `keywords`,
      content: keywords,
    },
    {
      name: `image`,
      content: seo.image,
    },
    {
      property: `og:title`,
      content: seo.title,
    },
    {
      property: `og:url`,
      content: seo.url,
    },
    {
      property: `og:description`,
      content: seo.description,
    },
    {
      property: `og:image`,
      content: seo.image,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      name: `twitter:card`,
      content: `summary_large_image`,
    },
    {
      name: `twitter:image`,
      content: seo.image,
    },
    {
      name: `twitter:creator`,
      content: twitterUsername,
    },
    {
      name: `twitter:title`,
      content: seo.title,
    },
    {
      name: `twitter:description`,
      content: seo.description,
    },
    ...meta,
  ];

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={seo.title}
      titleTemplate={seo.titleTemplate}
      meta={noindex ? [...metaTags, { name: "robots", content: "noindex,follow" }] : metaTags}
    >
      <link rel="canonical" href={canonicalHref} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Website",
          url: seo.url,
          name: seo.title,
        })}
      </script>
    </Helmet>
  );
}

export default SEO;
